export const DEVICE24_POLL_MS = 5 * 60 * 1000;
export const DEVICE24_CLICK_MS = 60 * 1000;

// One instance belongs to the persistent admin layout, never to a routed page.
export function createDevice24Poller({
  fetchDevices,
  publish,
  visibility = document,
  now = () => Date.now(),
  setTimer = setTimeout,
  clearTimer = clearTimeout,
  createController = () => new AbortController()
}) {
  let active = false;
  let generation = 0;
  let timer = null;
  let pending = null;
  let controller = null;
  let lastSuccess = null;

  const visible = () => visibility.visibilityState === "visible";
  function clearSchedule() {
    if (timer !== null) clearTimer(timer);
    timer = null;
  }
  function schedule(delay = DEVICE24_POLL_MS) {
    clearSchedule();
    if (active && visible()) {
      timer = setTimer(() => {
        timer = null;
        refresh();
      }, Math.max(1, delay));
    }
  }
  function refresh() {
    if (!active) return Promise.resolve();
    if (pending) return pending;
    clearSchedule();
    const requestGeneration = generation;
    controller = createController();
    const { signal } = controller;
    publish({ loading: true });
    pending = Promise.resolve()
      .then(() => {
        if (!active || generation !== requestGeneration) return;
        return fetchDevices({ signal });
      })
      .then((devices) => {
        if (!active || generation !== requestGeneration) return;
        lastSuccess = now();
        publish({ devices, error: "", hasLoaded: true });
      })
      .catch(() => {
        if (!active || generation !== requestGeneration || signal.aborted)
          return;
        publish({
          error: "알림을 갱신하지 못했습니다. 잠시 후 다시 시도해주세요."
        });
      })
      .finally(() => {
        if (!active || generation !== requestGeneration) return;
        pending = null;
        controller = null;
        publish({ loading: false });
        // On failure, wait a full interval instead of retrying a stale timestamp immediately.
        schedule();
      });
    return pending;
  }
  function onVisibilityChange() {
    clearSchedule();
    if (!active || !visible()) return;
    if (lastSuccess === null || now() - lastSuccess >= DEVICE24_POLL_MS)
      refresh();
    else if (!pending) schedule(DEVICE24_POLL_MS - (now() - lastSuccess));
  }
  function onBellClick() {
    if (lastSuccess === null || now() - lastSuccess >= DEVICE24_CLICK_MS) {
      return refresh();
    }
    return Promise.resolve();
  }
  function start() {
    if (active) return;
    active = true;
    generation++;
    visibility.addEventListener("visibilitychange", onVisibilityChange);
    refresh();
  }
  function stop() {
    active = false;
    generation++;
    clearSchedule();
    visibility.removeEventListener("visibilitychange", onVisibilityChange);
    controller?.abort();
    controller = null;
    pending = null;
    lastSuccess = null;
    publish({ devices: [], loading: false, error: "", hasLoaded: false });
  }
  return { start, stop, refresh, onBellClick };
}
